import React, { useState } from 'react';
import { Database, HardDrive, FolderTree, Activity, RefreshCw, CheckCircle2, AlertTriangle, PieChart, ChevronDown, ChevronUp } from 'lucide-react';
import { useStorageUsage } from '../hooks/useStorageUsage';

const EXPANDED_KEY = 'vendetta-storage-expanded';

export const StorageCounter: React.FC = () => {
  const { stats, loading, refreshing, error, fetchStats } = useStorageUsage();
  // Persist collapsed/expanded state so the choice sticks across page loads.
  const [expanded, setExpanded] = useState<boolean>(() => {
    try { return localStorage.getItem(EXPANDED_KEY) === '1' } catch { return false }
  });
  const toggleExpanded = () => {
    setExpanded(prev => {
      const next = !prev
      try { localStorage.setItem(EXPANDED_KEY, next ? '1' : '0') } catch { /* ignore */ }
      return next
    })
  };

  const formatSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatNumber = (num: number) => num.toLocaleString();

  if (loading) {
    return (
      <div className="rounded-xl border border-dark-700 bg-dark-800/50 backdrop-blur-sm p-4">
        <div className="animate-pulse space-y-3">
          <div className="h-3 bg-dark-700 rounded w-1/3"></div>
          <div className="h-6 bg-dark-700 rounded"></div>
          <div className="h-6 bg-dark-700 rounded"></div>
        </div>
      </div>
    );
  }

  if (error || !stats) {
    return (
      <div className="rounded-xl border border-dark-700 bg-dark-800/50 backdrop-blur-sm p-4">
        <div className="flex items-start gap-3 mb-3">
          <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm text-red-400 font-medium mb-1">Storage Unavailable</p>
            <p className="text-xs text-dark-400">{error || 'Failed to load stats'}</p>
          </div>
        </div>
        {!error?.includes('login') && (
          <button 
            onClick={fetchStats} 
            className="btn btn-sm btn-secondary w-full"
          >
            <RefreshCw className="w-3 h-3 mr-2" />
            Retry
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-dark-700 bg-dark-800/50 backdrop-blur-sm overflow-hidden">
      {/* Header — clickable to expand/collapse. Compact form shows the two
          most-glance-worthy numbers (files + size) so the widget stays useful
          even when body is hidden. */}
      <div
        className={`p-3 flex items-center justify-between cursor-pointer transition ${expanded ? 'border-b border-dark-700' : 'hover:bg-dark-800/70'}`}
        onClick={toggleExpanded}
      >
        <div className="flex items-center gap-2 min-w-0">
          <Activity className="w-4 h-4 text-primary-400 flex-shrink-0" />
          <h3 className="text-sm font-semibold text-white">Storage</h3>
          {!expanded && (
            <span className="text-xs text-dark-400 truncate">
              · {formatNumber(stats.usage.totalFiles)} · {formatSize(stats.usage.totalSize)}
              {stats.r2 && !stats.r2.synced && (
                <AlertTriangle className="w-3 h-3 inline ml-1.5 -mt-0.5 text-yellow-400" />
              )}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {expanded && (
            <button
              onClick={(e) => { e.stopPropagation(); fetchStats() }}
              disabled={refreshing}
              className="p-1 rounded hover:bg-dark-700 disabled:opacity-50 transition-colors"
              title="Refresh stats"
            >
              <RefreshCw className={`w-3.5 h-3.5 text-dark-400 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
          )}
          {expanded
            ? <ChevronUp className="w-4 h-4 text-dark-400" />
            : <ChevronDown className="w-4 h-4 text-dark-400" />}
        </div>
      </div>

      {/* Everything below is only rendered when expanded. Sync warning + all
          detail sections. */}
      {expanded && stats.r2 && !stats.r2.synced && (
        <div className="px-4 py-2 bg-yellow-500/10 border-b border-yellow-500/20">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-yellow-400 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-yellow-400">
              Orphan detected — DB tracks {stats.usage.totalFiles} active
              {(stats.usage.trashedFiles ?? 0) > 0 && ` + ${stats.usage.trashedFiles} trashed`},
              R2 holds {stats.r2.files}
            </p>
          </div>
        </div>
      )}
      {expanded && (
      <>
      {/* Stats Grid */}
      <div className="p-4 space-y-4">
        
        {/* Database Stats */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Database className="w-4 h-4 text-purple-400" />
            <span className="text-xs font-medium text-dark-300">Database (D1)</span>
          </div>
          
          <div className="space-y-2">
            <div className="flex justify-between items-center text-xs">
              <span className="text-dark-400">Files</span>
              <span className="font-semibold text-white">{formatNumber(stats.usage.totalFiles)}</span>
            </div>
            
            <div className="flex justify-between items-center text-xs">
              <span className="text-dark-400">Size</span>
              <span className="font-semibold text-white">{formatSize(stats.usage.totalSize)}</span>
            </div>
            
            <div className="flex justify-between items-center text-xs">
              <span className="text-dark-400">Recent</span>
              <span className="font-semibold text-white">{formatNumber(stats.usage.recentFiles)}</span>
            </div>

            {(stats.usage.trashedFiles ?? 0) > 0 && (
              <div className="flex justify-between items-center text-xs">
                <span className="text-dark-400">In trash</span>
                <span className="font-semibold text-yellow-400">
                  {formatNumber(stats.usage.trashedFiles!)}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Type Breakdown — visualise which content type dominates storage.
             Data comes from the same /api/storage/usage payload, no extra call. */}
        {stats.breakdown?.byType && Object.keys(stats.breakdown.byType).length > 0 && (
          <div className="pt-4 border-t border-dark-700">
            <div className="flex items-center gap-2 mb-3">
              <PieChart className="w-4 h-4 text-emerald-400" />
              <span className="text-xs font-medium text-dark-300">By Type</span>
            </div>
            <div className="space-y-2">
              {Object.entries(stats.breakdown.byType)
                .sort(([, a], [, b]) => b.size - a.size)
                .map(([type, info]) => {
                  const pct = stats.usage.totalSize > 0
                    ? (info.size / stats.usage.totalSize) * 100
                    : 0
                  const colors: Record<string, string> = {
                    images: 'bg-emerald-500',
                    videos: 'bg-purple-500',
                    audio: 'bg-pink-500',
                    documents: 'bg-blue-500',
                    archives: 'bg-amber-500',
                    other: 'bg-dark-500',
                  }
                  const bar = colors[type] ?? 'bg-dark-500'
                  return (
                    <div key={type}>
                      <div className="flex justify-between items-center text-xs mb-1">
                        <span className="text-dark-400 capitalize">{type}</span>
                        <span className="font-medium text-white">
                          {formatSize(info.size)} <span className="text-dark-500">({info.count})</span>
                        </span>
                      </div>
                      <div className="h-1.5 bg-dark-900 rounded-full overflow-hidden">
                        <div
                          className={`h-full ${bar} transition-all duration-500`}
                          style={{ width: `${pct.toFixed(1)}%` }}
                        />
                      </div>
                    </div>
                  )
                })}
            </div>
          </div>
        )}

        {/* R2 Stats */}
        {stats.r2 && (
          <div className="pt-4 border-t border-dark-700">
            <div className="flex items-center gap-2 mb-3">
              <HardDrive className="w-4 h-4 text-primary-400" />
              <span className="text-xs font-medium text-dark-300">Storage (R2)</span>
            </div>
            
            <div className="space-y-2">
              <div className="flex justify-between items-center text-xs">
                <span className="text-dark-400">Files</span>
                <span className="font-semibold text-white">{formatNumber(stats.r2.files)}</span>
              </div>
              
              <div className="flex justify-between items-center text-xs">
                <span className="text-dark-400">Size</span>
                <span className="font-semibold text-white">{formatSize(stats.r2.size)}</span>
              </div>
              
              <div className="pt-2 border-t border-dark-700">
                <div className="text-xs text-dark-400 mb-2">Operations</div>
                <div className="space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="text-dark-500">Class A</span>
                    <span className="font-medium text-dark-300">{formatNumber(stats.r2.operations.classA)}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-dark-500">Class B</span>
                    <span className="font-medium text-dark-300">{formatNumber(stats.r2.operations.classB)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer Status */}
      <div className="px-4 py-3 bg-dark-800/50 border-t border-dark-700">
        <div className="flex items-center gap-2">
          <FolderTree className="w-3 h-3 text-dark-500 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            {stats.r2?.synced ? (
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-3 h-3 text-primary-400 flex-shrink-0" />
                <span className="text-xs text-primary-400 font-medium">All systems synchronized</span>
              </div>
            ) : stats.r2 ? (
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-3 h-3 text-yellow-400 flex-shrink-0" />
                <span className="text-xs text-yellow-400 font-medium">Sync mismatch detected</span>
              </div>
            ) : (
              <span className="text-xs text-dark-500">R2 sync status unavailable</span>
            )}
          </div>
        </div>
      </div>
      </>
      )}
    </div>
  );
};